/**
 * RecentQrCodes
 * Displays the last 10 QR codes in a card with a simple list layout.
 */

import { useNavigate } from 'react-router-dom';
import {
  Stack, Text, Group, Card, Button, Image,
} from '@/components/ui';
import { Divider } from '@/components/ui/data-display';
import { ScrollArea } from '@/components/ui/layout';
import {
  IconArrowRight, IconQrcode, IconScan,
} from '@tabler/icons-react';
import { QrCodeStatusBadge, QrCodeTypeBadge } from '../../components';
import { formatDate, formatScanCount } from '../../utils/qrCodeHelpers';

interface RecentQrCodesProps {
  codes: any[];
}

export default function RecentQrCodes({ codes }: RecentQrCodesProps) {
  const navigate = useNavigate();

  const goToCode = (id: string | number) => {
    navigate(`/app/qr-codes/view/${id}`);
  };

  return (
    <Card shadow="sm" padding="lg" radius="md" withBorder>
      <Group justify="space-between" mb="sm">
        <Group gap="xs">
          <IconQrcode size={20} />
          <Text size="lg" fw={600}>
            Recent QR Codes
          </Text>
        </Group>
        <Button
          variant="subtle"
          size="xs"
          rightSection={<IconArrowRight size={14} />}
          onClick={() => navigate('/app/qr-codes/list')}
        >
          View All
        </Button>
      </Group>
      <Divider mb="sm" />
      <ScrollArea h={420}>
        {codes.length === 0 ? (
          <Stack align="center" py="xl" gap="sm">
            <IconQrcode size={48} stroke={1.5} color="var(--mantine-color-gray-5)" />
            <Text size="sm" c="dimmed">
              No QR codes yet. Create your first one!
            </Text>
            <Button
              variant="light"
              size="sm"
              onClick={() => navigate('/app/qr-codes/create')}
            >
              Create QR Code
            </Button>
          </Stack>
        ) : (
          <Stack gap="xs">
            {codes.slice(0, 10).map((code) => (
              <Card
                key={code.id || code.qrCodeId}
                padding="sm"
                withBorder
                radius="sm"
                style={{ cursor: 'pointer' }}
                onClick={() => goToCode(code.id || code.qrCodeId)}
              >
                <Group justify="space-between" wrap="nowrap">
                  <Group gap="sm" wrap="nowrap" style={{ flex: 1, minWidth: 0 }}>
                    {code.imageUrl || code.qrImageUrl ? (
                      <Image
                        src={code.imageUrl || code.qrImageUrl}
                        alt={code.name || code.title}
                        w={40}
                        h={40}
                        radius="sm"
                        fit="contain"
                        style={{ flexShrink: 0 }}
                      />
                    ) : (
                      <IconQrcode
                        size={40}
                        stroke={1}
                        color="var(--mantine-color-gray-5)"
                        style={{ flexShrink: 0 }}
                      />
                    )}
                    <Stack gap={2} style={{ flex: 1, minWidth: 0 }}>
                      <Text size="sm" fw={600} lineClamp={1}>
                        {code.name || code.title || 'Untitled'}
                      </Text>
                      <Group gap={6}>
                        <QrCodeTypeBadge type={code.type || code.qrCodeType} size="xs" />
                        <QrCodeStatusBadge status={code.status || 'Active'} size="xs" />
                      </Group>
                    </Stack>
                  </Group>
                  <Stack gap={2} align="flex-end" style={{ flexShrink: 0 }}>
                    <Group gap={4}>
                      <IconScan size={14} color="var(--mantine-color-dimmed)" />
                      <Text size="xs" c="dimmed">
                        {formatScanCount(code.scanCount ?? code.totalScans ?? 0)}
                      </Text>
                    </Group>
                    <Text size="xs" c="dimmed">
                      {formatDate(code.createdDate || code.createdAt)}
                    </Text>
                  </Stack>
                </Group>
              </Card>
            ))}
          </Stack>
        )}
      </ScrollArea>
    </Card>
  );
}
